import { apiUrl } from '../config';

const VISITOR_KEY = 'browmie_analytics_visitor';
const SESSION_KEY = 'browmie_analytics_session';
const TOUCH_KEY = 'browmie_analytics_touch';
const TOUCH_TTL_MS = 30 * 24 * 60 * 60 * 1000;
const recent = new Map();

const randomId = (prefix) => {
  try {
    if (window.crypto?.randomUUID) return `${prefix}_${window.crypto.randomUUID()}`;
    const bytes = window.crypto.getRandomValues(new Uint8Array(16));
    return `${prefix}_${Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('')}`;
  } catch {
    return `${prefix}_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 14)}`;
  }
};

const storageId = (storage, key, prefix) => {
  try {
    const saved = storage.getItem(key);
    if (saved && /^[A-Za-z0-9:_-]{8,96}$/.test(saved)) return saved;
    const id = randomId(prefix);
    storage.setItem(key, id);
    return id;
  } catch {
    return randomId(prefix);
  }
};

const visitorId = () => storageId(localStorage, VISITOR_KEY, 'v');
const sessionId = () => storageId(sessionStorage, SESSION_KEY, 's');

const cleanText = (value, max = 160) => {
  const text = String(value == null ? '' : value).trim();
  return text ? text.slice(0, max) : '';
};

const currentTouch = () => {
  try {
    const params = new URLSearchParams(window.location.search);
    const incoming = {
      source: cleanText(params.get('utm_source'), 120),
      medium: cleanText(params.get('utm_medium'), 120),
      campaign: cleanText(params.get('utm_campaign'), 160),
      content: cleanText(params.get('utm_content'), 160),
      term: cleanText(params.get('utm_term'), 160),
    };
    if (Object.values(incoming).some(Boolean)) {
      localStorage.setItem(TOUCH_KEY, JSON.stringify({ ...incoming, at: Date.now() }));
      return incoming;
    }
    const saved = JSON.parse(localStorage.getItem(TOUCH_KEY) || '{}');
    if (saved.at && Date.now() - saved.at < TOUCH_TTL_MS) {
      return {
        source: cleanText(saved.source, 120), medium: cleanText(saved.medium, 120),
        campaign: cleanText(saved.campaign, 160), content: cleanText(saved.content, 160),
        term: cleanText(saved.term, 160),
      };
    }
    localStorage.removeItem(TOUCH_KEY);
  } catch { /* storage and URL parsing are optional */ }
  return { source: '', medium: '', campaign: '', content: '', term: '' };
};

const deviceType = () => {
  try {
    const ua = navigator.userAgent || '';
    const width = window.innerWidth || 1024;
    if (/iPad|Tablet|PlayBook|Silk/i.test(ua) || (width >= 600 && width < 1024)) return 'tablet';
    if (/Mobi|Android|iPhone|iPod/i.test(ua) || width < 600) return 'mobile';
    return 'desktop';
  } catch {
    return 'unknown';
  }
};

const safeReferrer = () => {
  try {
    if (!document.referrer) return '';
    const ref = new URL(document.referrer);
    return `${ref.origin}${ref.pathname}`;
  } catch {
    return '';
  }
};

export const getAnalyticsContext = () => ({
  visitorId: visitorId(),
  sessionId: sessionId(),
  path: window.location.pathname,
  referrer: safeReferrer(),
  language: document.documentElement.lang || navigator.language || '',
  deviceType: deviceType(),
  utm: currentTouch(),
});

const dedupeKey = (eventName, productId, properties) => [
  eventName,
  productId || '',
  properties?.step || '',
  properties?.outcome || '',
  window.location.pathname,
].join('|');

// Fire-and-forget by design: analytics must never delay a cart or payment. The
// endpoint accepts idempotent event ids, while a short client dedupe window
// absorbs React StrictMode's development-only double effects.
export const trackEvent = (eventName, properties = {}, {
  productId = null,
  transactionId = null,
  dedupe = false,
} = {}) => {
  try {
    const key = dedupeKey(eventName, productId, properties);
    const now = Date.now();
    if (dedupe && now - (recent.get(key) || 0) < 750) return Promise.resolve(false);
    recent.set(key, now);
    if (recent.size > 200) {
      for (const [oldKey, at] of recent) if (now - at > 60000) recent.delete(oldKey);
    }
    const payload = {
      eventId: randomId('e'),
      eventName,
      occurredAt: new Date(now).toISOString(),
      ...getAnalyticsContext(),
      ...(productId ? { productId: Number(productId) } : {}),
      ...(transactionId ? { transactionId: Number(transactionId) } : {}),
      properties,
    };
    return fetch(apiUrl('/api/system/public/events'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).then((response) => response.ok).catch(() => false);
  } catch {
    return Promise.resolve(false);
  }
};

export const trackCheckoutStep = (step, properties = {}, options = {}) =>
  trackEvent('checkout_step', { ...properties, step }, { ...options, dedupe: options.dedupe ?? true });

export const trackPaymentOutcome = (outcome, properties = {}, transactionId = null) =>
  trackEvent('payment_outcome', { ...properties, outcome }, { transactionId, dedupe: true });

export const _analyticsTest = { randomId, deviceType, currentTouch };
